// Base API configuration using native fetch (no polyfills needed)
const API_BASE_URL = 'https://api.thrivecaremarket.co.uk/api';;

// Global variable to store token getter function
let getToken: () => string | null = () => {
  // Default: get token from localStorage
  return localStorage.getItem('accessToken') || localStorage.getItem('token');
};

// Function to set custom token getter (useful if you switch to Redux or Context)
export const setTokenGetter = (getter: () => string | null) => {
  getToken = getter;
};

// Function to get auth token
const getAuthToken = (): string | null => {
  return getToken();
};

// Function to set auth token (for backward compatibility)
export const setAuthToken = (token: string | null) => {
  // Token is managed via getToken function
  if (token) {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
  }
};

// Global variable to store logout handler
let logoutHandler: (() => void) | null = null;

// Function to set logout handler
export const setLogoutHandler = (handler: () => void) => {
  logoutHandler = handler;
};

// Helper function to handle response
const handleResponse = async (response: Response): Promise<any> => {
  // Handle 401 errors
  if (response.status === 401) {
    console.log('🚨 Unauthorized - Token expired or invalid');
    
    // Clear token
    setAuthToken(null);
    localStorage.removeItem('user');
    
    // Call logout handler if available
    if (logoutHandler) {
      logoutHandler();
    } else {
      // Fallback: redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    throw new Error('Unauthorized');
  }

  // Handle network errors
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return response.text();
};

// API client using fetch
const api = {
  get: async (url: string, config?: { params?: Record<string, any> }): Promise<any> => {
    const token = getAuthToken();
    const queryString = config?.params
      ? '?' + new URLSearchParams(config.params).toString()
      : '';
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}${queryString}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      if (!error.response) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  },

  post: async (url: string, data?: any): Promise<any> => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: data ? JSON.stringify(data) : undefined,
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      if (!error.response) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  },

  patch: async (url: string, data?: any): Promise<any> => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: data ? JSON.stringify(data) : undefined,
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      if (!error.response) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  },

  put: async (url: string, data?: any): Promise<any> => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: data ? JSON.stringify(data) : undefined,
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      if (!error.response) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  },

  delete: async (url: string): Promise<any> => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      if (!error.response) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  },
};

export default api;
